/* eslint-env browser */
// Transaction logs › Finance & admin controls (wave 3, W2 look).
//
// Two admin-only bulk tools and the finance activity report:
// - Zero out negative balances: credits every driver whose topUpBalance is
//   below zero with exactly |balance|, one addToWallet(driverId, amount,
//   adminUid, 'admin_credit') per driver, in list order, awaited one by one.
// - Bulk top-up: credits every driver whose topUpBalance is exactly zero
//   with the same amount (1 to 1,000), addToWallet(driverId, amount,
//   adminUid, 'bulk_topup') per driver, in list order, awaited one by one.
// Both ask first with a dialog that names the number of drivers and the
// total that will be credited (the write itself is unchanged).
// - Finance activity report (admin and finance): the wallet entries those
//   two tools wrote, read from the wallet history the parent Transaction
//   logs tab already fetched (no read of its own), with date and type
//   filters and CSV / print exports.
// Call-centre feedback (29 Oct 2026): each report date clears with its own
// X, and a copy button beside each driver's number copies it without spaces
// (+251964540448) for MicroSIP; it is not there while numbers are hidden.
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import BalanceRoundedIcon from '@mui/icons-material/BalanceRounded';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { api } from 'common';
import { tokens as c } from '../theme';
import { confirmDialog, InlineMessage } from '../components/feedback';
import {
  DataTable,
  PageHeader,
  SectionCard,
  FieldLabel,
  FieldError,
  SrOnly,
  ChipGroup,
  ActiveFilters,
  CopyButton,
  InfoRow,
  ALL,
  formatMoney,
  formatNumber,
  formatPhoneDisplay,
} from '../components/modern';
import DateFilterField from '../components/modern/DateFilterField';
import { formatAddisDate, ADDIS_OFFSET_MS } from '../common/exportUtils';

const REMARK_ADMIN_CREDIT = 'admin_credit';
const REMARK_BULK_TOPUP = 'bulk_topup';
const DAY_MS = 24 * 60 * 60 * 1000;

// The start of the Addis Ababa day that contains `ms`.
const addisDayStart = (ms) => {
  const shifted = Number(ms) + ADDIS_OFFSET_MS;
  return shifted - (((shifted % DAY_MS) + DAY_MS) % DAY_MS) - ADDIS_OFFSET_MS;
};

// A big number on a quiet tile (count of drivers, total to credit).
function Fact({ label, value, tone = 'neutral' }) {
  const fg = tone === 'amber' ? c.AMBER_TEXT : tone === 'success' ? c.SUCCESS_TEXT : tone === 'info' ? c.INFO_TEXT : c.INK;
  const bg = tone === 'amber' ? c.AMBER_TINT : tone === 'success' ? c.SUCCESS_TINT : tone === 'info' ? c.INFO_TINT : c.SURFACE_ALT;
  return (
    <Box sx={{ bgcolor: bg, borderRadius: '10px', px: 1.75, py: 1.25, minWidth: 0 }}>
      <Typography component="p" sx={{ fontSize: 13, fontWeight: 600, color: fg, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
        {label}
      </Typography>
      <Typography component="p" variant="numeral" sx={{ color: fg, overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
    </Box>
  );
}

// Progress of a running bulk tool: a bar and "42% complete".
function Progress({ value, label }) {
  return (
    <Box sx={{ mt: 2 }} role="status" aria-live="polite">
      <LinearProgress variant="determinate" value={value} sx={{ height: 8, borderRadius: '4px' }} aria-label={label} />
      <Typography variant="body2" sx={{ mt: 0.75, color: c.INK_2, fontVariantNumeric: 'tabular-nums' }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function FinanceAdminControls() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const usersdata = useSelector((state) => state.usersdata);
  const auth = useSelector((state) => state.auth);
  const { addToWallet } = api;

  const isAdmin = auth.profile?.usertype === 'admin';
  const allowPrivate = !!(settings && settings.AllowCriticalEditsAdmin);

  // Zero-out state
  const [zeroOutProcessing, setZeroOutProcessing] = useState(false);
  const [zeroOutProgress, setZeroOutProgress] = useState(0);
  const [zeroOutResult, setZeroOutResult] = useState(null);

  // Bulk top-up state ('' or the number parseFloat made of the box, as before)
  const [bulkTopUpAmount, setBulkTopUpAmount] = useState('');
  const [topUpProcessing, setTopUpProcessing] = useState(false);
  const [topUpProgress, setTopUpProgress] = useState(0);
  const [topUpResult, setTopUpResult] = useState(null);

  // Finance activity report filters (dates are the start of an Addis day, ms)
  const [reportDateFrom, setReportDateFrom] = useState(null);
  const [reportDateTo, setReportDateTo] = useState(null);
  const [reportTypeFilter, setReportTypeFilter] = useState(ALL);

  const money = (value) => formatMoney(value, settings, true);

  // Drivers with negative topUpBalance only (strictly < 0)
  const negativeBalanceDrivers = useMemo(() => {
    if (!usersdata.users) return [];
    return usersdata.users.filter((u) => u.usertype === 'driver' && (parseFloat(u.topUpBalance) || 0) < 0);
  }, [usersdata.users]);

  // Drivers with zero balance (eligible for top-up buffer)
  const zeroBalanceDrivers = useMemo(() => {
    if (!usersdata.users) return [];
    return usersdata.users.filter((u) => u.usertype === 'driver' && (parseFloat(u.topUpBalance) || 0) === 0);
  }, [usersdata.users]);

  // What a zero-out would credit in total (the sum of |balance|).
  const negativeTotal = useMemo(
    () => negativeBalanceDrivers.reduce((sum, d) => sum + Math.abs(parseFloat(d.topUpBalance) || 0), 0),
    [negativeBalanceDrivers]
  );

  // Action 1: Zero out negative balances (the same loop as before).
  const handleZeroOut = useCallback(
    async (drivers) => {
      const adminUid = auth.profile?.uid || 'unknown';
      setZeroOutProcessing(true);
      setZeroOutProgress(0);
      setZeroOutResult(null);

      let completed = 0;
      let zeroed = 0;
      const errors = [];

      for (const driver of drivers) {
        try {
          const prevBalance = parseFloat(driver.topUpBalance) || 0;
          const correctionAmount = Math.abs(prevBalance);
          if (correctionAmount > 0) {
            await dispatch(addToWallet(driver.id, correctionAmount, adminUid, REMARK_ADMIN_CREDIT));
            zeroed++;
          }
        } catch (err) {
          errors.push({ driverId: driver.id, error: err.message });
        }
        completed++;
        setZeroOutProgress(Math.round((completed / drivers.length) * 100));
      }

      setZeroOutProcessing(false);
      setZeroOutProgress(100);
      setZeroOutResult({ zeroed, errors });
    },
    [dispatch, addToWallet, auth.profile]
  );

  // Action 2: Bulk top-up all zero-balance drivers (the same loop as before).
  const handleBulkTopUp = useCallback(
    async (drivers, amount) => {
      const adminUid = auth.profile?.uid || 'unknown';
      const topUpAmt = parseFloat(amount);
      if (isNaN(topUpAmt) || topUpAmt <= 0 || topUpAmt > 1000) return;

      setTopUpProcessing(true);
      setTopUpProgress(0);
      setTopUpResult(null);

      let completed = 0;
      let credited = 0;
      const errors = [];

      for (const driver of drivers) {
        try {
          await dispatch(addToWallet(driver.id, topUpAmt, adminUid, REMARK_BULK_TOPUP));
          credited++;
        } catch (err) {
          errors.push({ driverId: driver.id, error: err.message });
        }
        completed++;
        setTopUpProgress(Math.round((completed / drivers.length) * 100));
      }

      setTopUpProcessing(false);
      setTopUpProgress(100);
      setTopUpResult({ credited, errors });
    },
    [dispatch, addToWallet, auth.profile]
  );

  const summaryBody = (rows, warning) => (
    <Box>
      <Box component="dl" sx={{ m: 0, mb: 2 }}>
        {rows.map((r) => (
          <InfoRow key={r.label} label={r.label}>
            <Box component="span" sx={{ fontWeight: r.strong ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>
              {r.value}
            </Box>
          </InfoRow>
        ))}
      </Box>
      <InlineMessage kind="warning">{warning}</InlineMessage>
    </Box>
  );

  const askZeroOut = async () => {
    if (zeroOutProcessing || negativeBalanceDrivers.length === 0) return;
    const drivers = negativeBalanceDrivers;
    const total = drivers.reduce((sum, d) => sum + Math.abs(parseFloat(d.topUpBalance) || 0), 0);
    const ok = await confirmDialog({
      title: t('finance_zero_out_confirm_title', {
        count: drivers.length,
        total: money(total),
        defaultValue_one: 'Credit 1 driver {{total}}?',
        defaultValue_other: 'Credit {{count}} drivers a total of {{total}}?',
        defaultValue: 'Credit {{count}} drivers a total of {{total}}?',
      }),
      body: summaryBody(
        [
          { label: t('drivers_negative_balance'), value: formatNumber(drivers.length) },
          { label: t('total_credit_amount'), value: money(total), strong: true },
        ],
        `${t('zero_out_description', { count: drivers.length })}. ${t('bulk_operation_warning')}`
      ),
      confirmLabel: t('zero_out_action'),
      cancelLabel: t('cancel'),
      destructive: true,
    });
    if (ok) handleZeroOut(drivers);
  };

  const amountInvalid = bulkTopUpAmount !== '' && (isNaN(bulkTopUpAmount) || bulkTopUpAmount <= 0 || bulkTopUpAmount > 1000);
  const topUpDisabled = topUpProcessing || !bulkTopUpAmount || bulkTopUpAmount <= 0 || bulkTopUpAmount > 1000;
  const topUpTotal = zeroBalanceDrivers.length * (parseFloat(bulkTopUpAmount) || 0);

  const askBulkTopUp = async () => {
    if (topUpDisabled) return;
    const drivers = zeroBalanceDrivers;
    const amount = bulkTopUpAmount;
    const total = drivers.length * (parseFloat(amount) || 0);
    const ok = await confirmDialog({
      title: t('finance_bulk_topup_confirm_title', {
        count: drivers.length,
        total: money(total),
        defaultValue_one: 'Credit 1 driver {{total}}?',
        defaultValue_other: 'Credit {{count}} drivers a total of {{total}}?',
        defaultValue: 'Credit {{count}} drivers a total of {{total}}?',
      }),
      body: summaryBody(
        [
          { label: t('total_drivers_to_credit'), value: formatNumber(drivers.length) },
          { label: t('topup_amount_per_driver'), value: money(amount) },
          { label: t('total_credit_amount'), value: money(total), strong: true },
        ],
        t('bulk_operation_warning')
      ),
      confirmLabel: t('start_bulk_topup'),
      cancelLabel: t('cancel'),
      destructive: true,
    });
    if (ok) handleBulkTopUp(drivers, amount);
  };

  // ── Finance activity report (from the wallet history already in the store)
  const walletHistory = auth.allWalletHistory;
  const reportLoading = walletHistory == null;
  const financeTransactions = useMemo(() => {
    if (walletHistory == null) return [];
    const driverMap = {};
    if (usersdata.users) {
      usersdata.users.forEach((u) => {
        driverMap[u.id] = {
          name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
          mobile: u.mobile || '',
        };
      });
    }
    const transactions = [];
    Object.keys(walletHistory).forEach((uid) => {
      const userHistory = walletHistory[uid];
      if (!userHistory || typeof userHistory !== 'object') return;
      Object.keys(userHistory).forEach((txKey) => {
        const tx = userHistory[txKey];
        if (!tx || typeof tx !== 'object') return;
        // Entries written by the two tools above (addToWallet with our remarks)
        if (tx.remark === REMARK_ADMIN_CREDIT || tx.remark === REMARK_BULK_TOPUP) {
          transactions.push({
            id: txKey,
            driverId: uid,
            driverName: driverMap[uid]?.name || uid,
            driverMobile: driverMap[uid]?.mobile || '',
            transactionType: tx.remark,
            amount: tx.amount,
            timestamp: tx.date,
            performedBy: tx.operatorUid || '-',
            note: tx.remark === REMARK_ADMIN_CREDIT ? 'Negative balance cleared by Finance/Admin' : 'Bulk top-up credit by Finance/Admin',
          });
        }
      });
    });
    transactions.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    return transactions;
  }, [walletHistory, usersdata.users]);

  const typeLabel = (type) => (type === REMARK_ADMIN_CREDIT ? t('admin_credit') : t('bulk_topup'));

  // Rows inside the chosen days (type chips count over these).
  const datedTransactions = useMemo(() => {
    let filtered = financeTransactions;
    if (reportDateFrom) {
      const from = addisDayStart(reportDateFrom);
      filtered = filtered.filter((tx) => tx.timestamp >= from);
    }
    if (reportDateTo) {
      const to = addisDayStart(reportDateTo) + DAY_MS - 1;
      filtered = filtered.filter((tx) => tx.timestamp <= to);
    }
    return filtered;
  }, [financeTransactions, reportDateFrom, reportDateTo]);

  const filteredFinanceTransactions = useMemo(
    () => (reportTypeFilter === ALL ? datedTransactions : datedTransactions.filter((tx) => tx.transactionType === reportTypeFilter)),
    [datedTransactions, reportTypeFilter]
  );

  const typeCounts = useMemo(() => {
    const out = { [REMARK_ADMIN_CREDIT]: 0, [REMARK_BULK_TOPUP]: 0 };
    datedTransactions.forEach((tx) => {
      out[tx.transactionType] = (out[tx.transactionType] || 0) + 1;
    });
    return out;
  }, [datedTransactions]);

  const hiddenText = t('hidden_demo');
  const phoneShown = (row) => (allowPrivate ? (row.driverMobile ? formatPhoneDisplay(row.driverMobile) : '') : hiddenText);
  const phoneWord = t('phone', { defaultValue: 'Phone number' });

  const columns = [
    { title: t('driver_name'), field: 'driverName', render: (row) => <Box component="span" sx={{ fontWeight: 600 }}>{row.driverName}</Box> },
    {
      title: t('driver_phone'),
      field: 'driverMobile',
      nowrap: true,
      searchValue: (row) => (allowPrivate ? row.driverMobile : ''),
      render: (row) => {
        const shown = phoneShown(row);
        if (!shown) return <Box component="span" sx={{ color: c.TEXT_3 }}>—</Box>;
        return (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: allowPrivate ? c.INK : c.TEXT_3, fontVariantNumeric: 'tabular-nums' }}>
            {shown}
            {allowPrivate ? <CopyButton value={row.driverMobile} phone what={phoneWord} sx={{ my: -0.5 }} /> : null}
          </Box>
        );
      },
    },
    {
      title: t('adjustment_type'),
      field: 'transactionType',
      type: 'status',
      sortValue: (row) => typeLabel(row.transactionType),
      searchValue: (row) => typeLabel(row.transactionType),
      status: (row) =>
        row.transactionType === REMARK_ADMIN_CREDIT
          ? { tone: 'amber', icon: <BalanceRoundedIcon />, label: typeLabel(row.transactionType) }
          : { tone: 'success', icon: <AddCardOutlinedIcon />, label: typeLabel(row.transactionType) },
    },
    {
      title: t('amount_applied'),
      field: 'amount',
      type: 'money',
      render: (row) => (
        <Box component="span" sx={{ fontWeight: 700, color: c.SUCCESS_TEXT }}>
          +{parseFloat(row.amount || 0).toFixed(2)}
        </Box>
      ),
    },
    { title: t('date'), field: 'timestamp', type: 'datetime', defaultSort: 'desc' },
    {
      title: t('performed_by'),
      field: 'performedBy',
      render: (row) => <Box component="span" sx={{ fontSize: 13, color: c.INK_2, fontVariantNumeric: 'tabular-nums' }}>{row.performedBy?.substring(0, 10)}</Box>,
    },
  ];

  // The report export: the columns of the old "Export CSV" button, in its
  // order and with its headings (times now in Addis Ababa, 12-hour clock).
  const exportColumns = [
    { title: 'Driver Name', field: 'driverName' },
    { title: 'Driver Phone', field: 'driverMobile', value: (row) => (allowPrivate ? row.driverMobile : hiddenText) },
    { title: 'Adjustment Type', field: 'transactionType' },
    { title: 'Amount Applied', field: 'amount', align: 'right' },
    { title: 'Timestamp', field: 'timestamp', type: 'datetime' },
    { title: 'Performed By', field: 'performedBy' },
    { title: 'Note', field: 'note' },
  ];

  const typeOptions = [
    { value: ALL, label: t('all'), count: datedTransactions.length },
    { value: REMARK_ADMIN_CREDIT, label: t('admin_credit'), count: typeCounts[REMARK_ADMIN_CREDIT] },
    { value: REMARK_BULK_TOPUP, label: t('bulk_topup'), count: typeCounts[REMARK_BULK_TOPUP] },
  ];

  const tokens = [];
  if (reportDateFrom) tokens.push({ key: 'from', group: t('date_from'), value: formatAddisDate(reportDateFrom) });
  if (reportDateTo) tokens.push({ key: 'to', group: t('date_to'), value: formatAddisDate(reportDateTo) });
  if (reportTypeFilter !== ALL) tokens.push({ key: 'type', group: t('transaction_type'), value: typeLabel(reportTypeFilter) });
  const removeToken = (key) => {
    if (key === 'from') setReportDateFrom(null);
    if (key === 'to') setReportDateTo(null);
    if (key === 'type') setReportTypeFilter(ALL);
  };
  const clearFilters = () => {
    setReportDateFrom(null);
    setReportDateTo(null);
    setReportTypeFilter(ALL);
  };

  const reportTitle = t('finance_activity_report');
  const countText = ({ shown, total, query, loading }) => {
    if (loading) return t('loading', { defaultValue: 'Loading…' });
    if (query || tokens.length) {
      return t('finance_report_count_shown', { shown: formatNumber(shown), total: formatNumber(financeTransactions.length), defaultValue: '{{shown}} of {{total}} records' });
    }
    return `${formatNumber(total)} ${t('records')}`;
  };

  const resultMessage = (result, doneLabel) =>
    result ? (
      <InlineMessage kind={result.errors.length > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
        {doneLabel}: {formatNumber(result.zeroed !== undefined ? result.zeroed : result.credited)}
        {result.errors.length > 0 ? ` · ${t('errors')}: ${formatNumber(result.errors.length)}` : ''}
      </InlineMessage>
    ) : null;

  const progressLabel = (value) => `${value}% ${t('complete')}`;

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Admin-only bulk tools */}
      {isAdmin ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'stretch' }}>
          {/* Zero out negative balances */}
          <SectionCard id="fac-zero" icon={<WarningAmberRoundedIcon />} hue="amber" title={t('zero_out_negative_balances')} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ color: c.INK_2, mt: 0.5, mb: 2 }}>
              {t('zero_out_help')}
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', mb: 2 }}>
              <Fact label={t('drivers_negative_balance')} value={formatNumber(negativeBalanceDrivers.length)} tone="amber" />
              <Fact label={t('total_credit_amount')} value={money(negativeTotal)} />
            </Box>
            <Box sx={{ mt: 'auto' }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<BalanceRoundedIcon />}
                disabled={zeroOutProcessing || negativeBalanceDrivers.length === 0}
                onClick={askZeroOut}
              >
                {zeroOutProcessing ? t('finance_bulk_running', { defaultValue: 'Crediting drivers…' }) : t('zero_out_action')}
              </Button>
              {zeroOutProcessing ? <Progress value={zeroOutProgress} label={progressLabel(zeroOutProgress)} /> : null}
              {resultMessage(zeroOutResult, t('drivers_zeroed'))}
            </Box>
          </SectionCard>

          {/* Bulk top-up credit */}
          <SectionCard id="fac-topup" icon={<AccountBalanceWalletOutlinedIcon />} hue="success" title={t('bulk_topup_credit')} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ color: c.INK_2, mt: 0.5, mb: 2 }}>
              {t('bulk_topup_help')}
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', mb: 2 }}>
              <Fact label={t('total_drivers_to_credit')} value={formatNumber(zeroBalanceDrivers.length)} tone="success" />
              <Fact label={t('total_credit_amount')} value={money(topUpTotal)} tone="info" />
            </Box>
            <Box sx={{ mb: 2 }}>
              <FieldLabel htmlFor="fac-topup-amount">{t('topup_amount_per_driver')}</FieldLabel>
              <TextField
                id="fac-topup-amount"
                type="number"
                fullWidth
                value={bulkTopUpAmount}
                onChange={(e) => {
                  const val = e.target.value;
                  setBulkTopUpAmount(val === '' ? '' : parseFloat(val));
                }}
                error={amountInvalid}
                disabled={topUpProcessing}
                inputProps={{
                  min: 1,
                  max: 1000,
                  inputMode: 'decimal',
                  'aria-describedby': amountInvalid ? 'fac-topup-amount-hint fac-topup-amount-error' : 'fac-topup-amount-hint',
                  'aria-invalid': amountInvalid ? 'true' : undefined,
                }}
                sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
              />
              <SrOnly id="fac-topup-amount-hint">{t('topup_amount_helper')}</SrOnly>
              <FieldError id="fac-topup-amount-error">{amountInvalid ? t('topup_amount_helper') : null}</FieldError>
            </Box>
            <Box sx={{ mt: 'auto' }}>
              <Button variant="contained" fullWidth startIcon={<AddCardOutlinedIcon />} disabled={topUpDisabled} onClick={askBulkTopUp}>
                {topUpProcessing ? t('finance_bulk_running', { defaultValue: 'Crediting drivers…' }) : t('start_bulk_topup')}
              </Button>
              {topUpProcessing ? <Progress value={topUpProgress} label={progressLabel(topUpProgress)} /> : null}
              {resultMessage(topUpResult, t('drivers_credited'))}
            </Box>
          </SectionCard>
        </Box>
      ) : null}

      {/* Finance activity report */}
      <PageHeader title={reportTitle} icon={<ReceiptLongOutlinedIcon />} hue="info" />
      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', rowGap: 2, columnGap: 3 }}>
        <DateFilterField id="fac-from" label={t('date_from')} value={reportDateFrom} onChange={setReportDateFrom} onClear={() => setReportDateFrom(null)} />
        <DateFilterField id="fac-to" label={t('date_to')} value={reportDateTo} onChange={setReportDateTo} onClear={() => setReportDateTo(null)} />
        <ChipGroup label={t('transaction_type')} value={reportTypeFilter} onChange={setReportTypeFilter} options={typeOptions} />
      </Paper>
      <DataTable
        title={reportTitle}
        rows={filteredFinanceTransactions}
        columns={columns}
        rowKey={(row) => `${row.driverId}_${row.id}`}
        loading={reportLoading}
        countText={countText}
        headerContent={tokens.length ? <ActiveFilters tokens={tokens} onRemove={removeToken} onClearAll={clearFilters} /> : null}
        searchPlaceholder={t('finance_report_search', { defaultValue: 'Search driver or phone' })}
        resortKey={allowPrivate ? 'private' : 'demo'}
        rowLabel={(row) => row.driverName}
        prefsKey="finance_activity"
        exportCsv
        exportPrint
        exportColumns={exportColumns}
        exportFileName="Finance_Activity"
        exportTitle={reportTitle}
        minWidth={820}
        maxHeight={null}
        emptyState={({ query }) =>
          query
            ? null
            : tokens.length
              ? {
                  icon: <FilterAltOffOutlinedIcon />,
                  title: t('finance_report_empty_filtered', { defaultValue: 'No credits match these filters' }),
                  actionLabel: t('clear_filters', { defaultValue: 'Clear filters' }),
                  onAction: clearFilters,
                }
              : {
                  icon: <ReceiptLongOutlinedIcon />,
                  title: t('finance_report_empty', { defaultValue: 'No bulk credits yet' }),
                  body: t('finance_report_empty_body', { defaultValue: 'Credits from the two tools above show here.' }),
                }
        }
      />
    </Box>
  );
}
